import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from '../TimesheetsPage.module.css'
import o from '../../components/Overlay.module.css'
import ov from './overlays.module.css'
import { apiJson, ApiError } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { PendingButton, useSlow, inertClass, Failed } from '../../components/pending'
import { addDaysLocal, dateLabel, fmtDur, fmtHm, fmtStamp, hm, shortDateLabel, typeHm, wallMinutes } from './time'
import { isAdjustment, isCrewEntry, type EntryZone, type Shift, type ShiftEvent, type ShiftMetrics, type Team } from './types'

// The shift editor slide-over (CONTROL V4 §7f, openShift / saveShift). The
// draft is edited locally; every figure it shows (recorded, gap, deduction,
// net payable, the engine's breaks for the new hours, per-session issues)
// comes from POST /draft-metrics, which runs the same shift-math the stored
// shift is read with. One Save is one audited adjustment event.

interface DraftSession {
  key: string
  id?: string
  team_id: string
  start_date?: string
  start: string
  finish: string
  finish_next_day: boolean
  // D6.13: which of a repeated clock time (the autumn change) is meant. Only
  // ever set by the person, after the server has asked.
  start_occurrence?: 'earlier' | 'later'
  finish_occurrence?: 'earlier' | 'later'
  // D6.13: a person's confirmation that a zone may be given to, or used for,
  // these times. Only set by ticking the box the server's question shows.
  confirm_zone?: boolean
}

interface ZoneProblem {
  index: number
  code: string
  which?: 'start' | 'finish'
  needs?: string[]
  zone?: string
  error: string
  options?: { occurrence: 'earlier' | 'later'; label: string }[]
}

interface ZoneOffer {
  index: number
  kind: 'NO_ZONE' | 'UNPROVEN_UTC'
  current_zone: string | null
  proposed_zone: string | null
  proposed_source_label: string | null
  site_name: string | null
  problem: { code: string; error: string } | null
}

interface DraftBreaks {
  paid: string
  unpaid: string
  manual: boolean
  recomputed?: boolean
  wasManual?: boolean
  prior?: { paid: string; unpaid: string } | null
}

interface Draft {
  sessions: DraftSession[]
  breaks: DraftBreaks
}

interface DraftResult {
  metrics: ShiftMetrics
  computed_breaks: { paid_minutes: number; unpaid_minutes: number }
  breaks: { paid_minutes: number; unpaid_minutes: number }
  unpaid_deduction_minutes: number
  net_payable_minutes: number
  session_issues: { index: number; issues: string[] }[]
  errors: { index: number; error: string }[]
  zone_problems?: ZoneProblem[]
  // Sessions whose times are not changing but whose zone is not proven, with what a confirmation would record.
  zone_unconfirmed?: ZoneOffer[]
}

interface Props {
  user: any
  shiftId: string
  teams: Team[]
  today: string
  onClose: () => void
  // Called after anything is written, so the list re-reads the day.
  onWritten: (flash?: string) => void
}

let keySeq = 0
const nextKey = () => 'k' + ++keySeq

function draftFrom(shift: Shift): Draft {
  return {
    sessions: shift.sessions.map((s) => ({
      key: nextKey(),
      id: s.id,
      team_id: s.team_id,
      start_date: s.start_date,
      start: s.start_time || '',
      finish: s.finish_time || '',
      finish_next_day: !!s.finish_next_day,
    })),
    breaks: {
      paid: String(shift.paid_minutes ?? 0),
      unpaid: String(shift.unpaid_minutes ?? 0),
      manual: !!(shift.paid_is_manual || shift.unpaid_is_manual),
    },
  }
}

// The line under a session row. A saved session shows the zone it was written in, found by its id and not its
// position (rows can be removed). An unrecorded one, or a row not yet saved, says which zone its times would be read
// in; a new row on a shift with one proven zone reads in that zone.
function sessionZoneNote(saved: Shift['sessions'][number] | undefined, isNew: boolean, zone: EntryZone | null, shiftZone: EntryZone | null): string {
  if (saved?.zone_name) return `Time zone: ${saved.zone_label}`
  const z = isNew && shiftZone ? shiftZone : zone
  if (z?.zone) return `Time zone: not recorded. If these times change they will be read in ${z.zone} (${z.source_label}).`
  if (z?.problem) return `Time zone: not recorded. ${z.problem.message}`
  return 'Time zone: not recorded'
}

const toMin = (v: string) => parseInt(v, 10) || 0

function sessionsPayload(d: Draft) {
  return d.sessions.map((s) => ({
    id: s.id,
    team_id: s.team_id,
    start_date: s.start_date,
    start_time: s.start,
    finish_time: s.finish.trim() ? s.finish : null,
    finish_next_day: s.finish_next_day,
    start_occurrence: s.start_occurrence,
    finish_occurrence: s.finish_occurrence,
    confirm_zone: s.confirm_zone ? true : undefined,
  }))
}

function sameSessions(a: DraftSession[], b: DraftSession[]) {
  if (a.length !== b.length) return false
  return a.every(
    (s, i) =>
      s.id === b[i].id &&
      s.team_id === b[i].team_id &&
      s.start === b[i].start &&
      s.finish === b[i].finish &&
      s.finish_next_day === b[i].finish_next_day &&
      (s.start_date || null) === (b[i].start_date || null)
  )
}

function sameBreaks(a: DraftBreaks, b: DraftBreaks) {
  return a.manual === b.manual && toMin(a.paid) === toMin(b.paid) && toMin(a.unpaid) === toMin(b.unpaid)
}

// D6.13: what the clock-change panel says. Wall-clock, elapsed and entered normal hours are three different
// things; only the entered figure is paid from, and only once the wage calculation has used it.
function dstSummary(dst: NonNullable<Shift['dst']>): string {
  const apart = `The wall-clock times are ${fmtDur(dst.wall_minutes ?? 0)} apart; the time that actually passed is ${fmtDur(dst.elapsed_minutes ?? 0)}.`
  if (dst.kind !== 'spring') {
    return `This shift crosses ${dst.kind === 'autumn' ? 'the autumn' : 'a'} clock change. ${apart} Its payable result cannot be established correctly yet, so it cannot be exported.`
  }
  const c = dst.calculation
  if (!c) return `This shift crosses the spring clock change. ${apart} ${dst.export_status?.message ?? ''}`.trim()
  if (c.state === 'not_entered') {
    return `This shift crosses the spring clock change. ${apart} Normal hours must be entered before this day can be paid or exported. Nothing has been assumed from the clock times.`
  }
  if (c.state === 'waiting') {
    return `This shift crosses the spring clock change. ${apart} Normal hours are entered and the day is waiting to be recalculated from them. It cannot be exported until that has finished.`
  }
  if (c.state === 'cannot_allocate') {
    return `This shift crosses the spring clock change. ${apart} ${c.message || 'The entered normal hours cannot be allocated across this shift.'} It cannot be exported until this is corrected.`
  }
  const adj = c.adjustment_minutes
  return (
    `This shift crosses the spring clock change. ${apart} It is paid from the normal hours entered (the gross clock length, break not yet taken off): payable ${fmtDur(c.payable_minutes)} after the unpaid break. ` +
    (adj === 0
      ? 'The entered hours match the wall-clock span, so nothing was added or taken off.'
      : `That is ${fmtDur(Math.abs(adj))} ${adj > 0 ? 'more' : 'less'} than the recorded wall-clock time, shared across the shift’s sessions in proportion to their recorded minutes.`) +
    ' The elapsed time is shown for information only and is not what is paid.'
  )
}

export function ShiftEditor({ user, shiftId, teams, today, onClose, onWritten }: Props) {
  const [shift, setShift] = useState<Shift | null>(null)
  const [base, setBase] = useState<Draft | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<DraftResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The zone a changed local time would be read in (D6.13); display only.
  const [zone, setZone] = useState<EntryZone | null>(null)
  // The zone a session added to this shift will take: the shift's own proven zone when it has exactly one, else
  // the same answer as `zone`. Only rows not yet saved use it.
  const [shiftZone, setShiftZone] = useState<EntryZone | null>(null)
  // D6.13 normal hours (spring-crossing shifts): typed as h and m, saved apart
  // from the session edit. Nothing here reaches the wage calculation yet.
  const [nhH, setNhH] = useState('')
  const [nhM, setNhM] = useState('')
  const [nhNote, setNhNote] = useState('')
  const [nhBusy, setNhBusy] = useState(false)
  const [nhMsg, setNhMsg] = useState<string | null>(null)
  const rebreakPending = useRef(false)
  const requestSeq = useRef(0)

  const teamName = useMemo(() => {
    const m = new Map(teams.map((t) => [t.id, t.name]))
    return (id: string) => m.get(id) || shift?.sessions.find((s) => s.team_id === id)?.team_name || '—'
  }, [teams, shift])

  async function load(resetDraft: boolean) {
    const s = await apiJson<Shift>(user, `/api/timesheets/${shiftId}`)
    setShift(s)
    if (resetDraft) {
      const d = draftFrom(s)
      setBase(d)
      setDraft(d)
      setResult(null)
    }
    return s
  }

  // Keyed on the account, not the user object: a fresh object for the same
  // person must not reload the shift and throw away an unsaved draft.
  useEffect(() => {
    let cancelled = false
    setShift(null)
    setDraft(null)
    setReason('')
    setError(null)
    apiJson<EntryZone>(user, '/api/timesheets/zone')
      .then((z) => !cancelled && setZone(z))
      .catch(() => {})
    setShiftZone(null)
    apiJson<EntryZone>(user, `/api/timesheets/zone?shift_id=${encodeURIComponent(shiftId)}`)
      .then((z) => !cancelled && setShiftZone(z))
      .catch(() => {})
    apiJson<Shift>(user, `/api/timesheets/${shiftId}`)
      .then((s) => {
        if (cancelled) return
        const d = draftFrom(s)
        setShift(s)
        setBase(d)
        setDraft(d)
        setResult(null)
      })
      .catch((e) => !cancelled && setError(e.message || 'Could not load this shift'))
    return () => {
      cancelled = true
    }
  }, [shiftId, user?.uid])

  const confirmedAny = !!draft?.sessions.some((s) => s.confirm_zone)
  const dirty = !!(draft && base && (!sameSessions(draft.sessions, base.sessions) || !sameBreaks(draft.breaks, base.breaks) || confirmedAny || reason.trim()))
  // A confirmation alone is a change (D6.13): it saves with no time altered, and is audited.
  const draftChanged = !!(draft && base && (!sameSessions(draft.sessions, base.sessions) || !sameBreaks(draft.breaks, base.breaks) || confirmedAny))
  // Ask the server what a confirmation would record, before anything is saved.
  const hasUnproven = !!shift?.sessions.some((s) => s.zone_proven === false)
  const slow = useSlow(busy)

  // The editor is a layer (h 425-458): Escape, the scrim, the × and Cancel
  // all ask before unsaved edits go, and its first field takes focus once the
  // shift has loaded. Nothing closes it while its write is out (h 3984-3992).
  const panelEl = useRef<HTMLElement>(null)
  const { requestClose: tryClose, confirm } = useLayer(true, onClose, { dirty, el: panelEl, modal: true, focusFirst: !!draft, busy })

  // Recompute the draft's figures on the server, debounced.
  useEffect(() => {
    if (!draft || !shift) return
    if (!draftChanged && !hasUnproven) {
      setResult(null)
      return
    }
    const seq = ++requestSeq.current
    const t = setTimeout(async () => {
      try {
        const body: any = { sessions: sessionsPayload(draft) }
        if (draft.breaks.manual) body.breaks = { paid_minutes: toMin(draft.breaks.paid), unpaid_minutes: toMin(draft.breaks.unpaid) }
        const r = await apiJson<DraftResult>(user, `/api/timesheets/${shift.id}/draft-metrics`, { body })
        if (seq !== requestSeq.current) return
        setResult(r)
        // V4 reBreak: changed hours re-run the break schedule, even over an
        // earlier manual figure, and say so; the old override stays restorable.
        if (rebreakPending.current) {
          rebreakPending.current = false
          const eng = r.computed_breaks
          setDraft((d) => {
            if (!d) return d
            const prev = d.breaks
            if (eng.paid_minutes === toMin(prev.paid) && eng.unpaid_minutes === toMin(prev.unpaid)) return d
            return {
              ...d,
              breaks: {
                paid: String(eng.paid_minutes),
                unpaid: String(eng.unpaid_minutes),
                manual: false,
                recomputed: true,
                wasManual: !!prev.manual,
                prior: prev.manual ? { paid: prev.paid, unpaid: prev.unpaid } : null,
              },
            }
          })
        }
      } catch (e: any) {
        if (seq === requestSeq.current) setError(e.message || 'Could not recalculate this shift')
      }
    }, 250)
    return () => clearTimeout(t)
  }, [draft, shift, draftChanged, hasUnproven])

  if (!shift || !draft) {
    return (
      <Overlay onBackdrop={onClose} panelRef={panelEl}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>Shift</h2>
          </div>
          <CloseX onClick={onClose} />
        </div>
        <div className={`${o.drawerBody} ${ov.editorBody}`}>{error ? <div className={o.danger}>{error}</div> : <div className={styles.empty}>Loading shift…</div>}</div>
      </Overlay>
    )
  }

  const m: ShiftMetrics = result ? result.metrics : (shift.metrics as ShiftMetrics)
  const net = result ? result.net_payable_minutes : shift.net_payable_minutes
  const deduction = result ? result.unpaid_deduction_minutes : shift.unpaid_deduction_minutes ?? 0
  const unpaid = toMin(draft.breaks.unpaid)
  const issuesAt = (i: number): string[] => {
    if (!result) return shift.sessions[i]?.issues || []
    const err = result.errors.find((e) => e.index === i)
    if (err) return [err.error.replace(/^Session \d+: /, '')]
    return result.session_issues.find((x) => x.index === i)?.issues || []
  }

  const setSessions = (fn: (s: DraftSession[]) => DraftSession[]) => {
    rebreakPending.current = true
    setDraft((d) => (d ? { ...d, sessions: fn(d.sessions.map((s) => ({ ...s }))) } : d))
  }
  const setSess = (i: number, k: 'team_id' | 'start' | 'finish', v: string) =>
    setSessions((ss) => {
      ss[i][k] = k === 'team_id' ? v : typeHm(v)
      // A changed time is a new question: an earlier answer about a repeated
      // clock time does not carry over to it.
      if (k === 'start') ss[i].start_occurrence = undefined
      if (k === 'finish') ss[i].finish_occurrence = undefined
      // Nor does a confirmation: it was given for the times as they were.
      if (k !== 'team_id') ss[i].confirm_zone = undefined
      return ss
    })
  const confirmZone = (i: number, on: boolean) =>
    setSessions((ss) => {
      ss[i].confirm_zone = on ? true : undefined
      return ss
    })
  const chooseOccurrence = (i: number, which: 'start' | 'finish', occ: 'earlier' | 'later') =>
    setSessions((ss) => {
      ss[i][which === 'start' ? 'start_occurrence' : 'finish_occurrence'] = occ
      return ss
    })
  // h 1753-1756: which calendar day a time falls on is said, never guessed
  // from the clock. The work date stays the shift's.
  const setNextDay = (i: number, which: 'start' | 'finish', on: boolean) =>
    setSessions((ss) => {
      if (which === 'finish') ss[i].finish_next_day = on
      else ss[i].start_date = on ? addDaysLocal(shift.work_date, 1) : shift.work_date
      return ss
    })
  const addSess = () =>
    setSessions((ss) => ss.concat([{ key: nextKey(), team_id: ss[ss.length - 1]?.team_id || teams[0]?.id || '', start: '', finish: '', finish_next_day: false }]))
  const delSess = (i: number) => setSessions((ss) => ss.filter((_, j) => j !== i))
  const setBr = (k: 'paid' | 'unpaid', v: string) => {
    rebreakPending.current = false // what the user types supersedes a pending re-apply
    setDraft((d) => (d ? { ...d, breaks: { ...d.breaks, [k]: v.replace(/[^0-9]/g, '').slice(0, 3), manual: true, recomputed: false } } : d))
  }
  const ackBreak = () => setDraft((d) => (d ? { ...d, breaks: { ...d.breaks, recomputed: false, prior: null, wasManual: false } } : d))
  const restoreBreak = () =>
    setDraft((d) => {
      const p = d?.breaks.prior
      return d && p ? { ...d, breaks: { paid: p.paid, unpaid: p.unpaid, manual: true, recomputed: false, prior: null, wasManual: false } } : d
    })
  // Return to computed asks the engine for these hours; never a stored figure.
  const resetBr = async () => {
    try {
      const r = await apiJson<DraftResult>(user, `/api/timesheets/${shift.id}/draft-metrics`, { body: { sessions: sessionsPayload(draft) } })
      rebreakPending.current = false
      setDraft((d) => (d ? { ...d, breaks: { paid: String(r.computed_breaks.paid_minutes), unpaid: String(r.computed_breaks.unpaid_minutes), manual: false } } : d))
    } catch (e: any) {
      setError(e.message)
    }
  }

  async function save() {
    if (!draft || !shift) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<any>(user, `/api/timesheets/${shift.id}/adjustments`, {
        body: {
          sessions: sessionsPayload(draft),
          breaks: draft.breaks.manual ? { mode: 'manual', paid_minutes: toMin(draft.breaks.paid), unpaid_minutes: toMin(draft.breaks.unpaid) } : { mode: 'computed' },
          reason: reason.trim(),
          base_adjustment_count: shift.adjustment_count,
        },
      })
      // Close first: closing clears the flash, and the save's own message must win.
      onClose()
      onWritten(r.outcome === 'unchanged' ? undefined : r.flash || r.note)
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : 'Could not save this shift')
    } finally {
      setBusy(false)
    }
  }

  async function saveNormalHours(clear: boolean) {
    if (!shift) return
    const h = nhH.trim() === '' ? NaN : Number(nhH)
    const mm = nhM.trim() === '' ? 0 : Number(nhM)
    if (!clear && !(Number.isInteger(h) && h >= 0 && Number.isInteger(mm) && mm >= 0 && mm < 60 && h * 60 + mm <= 1440)) {
      setError('Enter the normal hours as whole hours and minutes, up to 24 hours.')
      return
    }
    setNhBusy(true)
    setError(null)
    setNhMsg(null)
    try {
      const r = await apiJson<any>(user, `/api/timesheets/${shift.id}/normal-hours`, {
        body: { normal_minutes: clear ? null : h * 60 + mm, note: nhNote.trim() || undefined },
      })
      await load(false)
      setNhH('')
      setNhM('')
      setNhNote('')
      setNhMsg(r.note || (clear ? 'Normal hours cleared.' : 'Normal hours saved.'))
      onWritten(r.note)
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : 'Could not save the normal hours')
    } finally {
      setNhBusy(false)
    }
  }

  async function toggleExclude() {
    if (!shift) return
    setError(null)
    try {
      const r = await apiJson<any>(user, `/api/timesheets/${shift.id}/exclude`, { body: { exclude: !shift.exclude_from_wages } })
      await load(false)
      onWritten(r.note)
    } catch (e: any) {
      setError(e.message || 'Could not change wage inclusion')
    }
  }

  async function undo(ev: ShiftEvent) {
    if (!shift) return
    setError(null)
    try {
      const r = await apiJson<any>(user, `/api/timesheets/${shift.id}/undo`, { body: { event_id: ev.id } })
      await load(true)
      setReason('')
      onWritten(r.note)
    } catch (e: any) {
      setError(e.message || 'Could not undo that adjustment')
    }
  }

  // ── derived view ──
  const history = shift.adjustment_history || []
  const lastAdj = [...history].reverse().find(isAdjustment)
  // A reversal stores the exact instant it undid; say it the way the history
  // shows times (V4: 'Undo of ' + ev.at + ' adjustment').
  const eventReason = (ev: ShiftEvent) => {
    const target = ev.reverses ? history.find((e) => e.id === ev.reverses) : undefined
    return target ? `Undo of ${fmtStamp(target.timestamp)} ${isCrewEntry(target) ? 'Dataphyll Crew entry' : 'adjustment'}` : ev.reason
  }
  // Keyed to the day's state, not the calendar (h 1785-1787): a day stays
  // live until it is closed, and a closed day holds late events instead.
  const isLive = (shift.day_state ? shift.day_state === 'LIVE' : shift.work_date === today) && !!shift.incomplete
  const teamIds = teams.filter((t) => !t.is_induction).map((t) => t.id)

  // Timeline window padded to whole hours (V4 eTimeline).
  const segs = (m.intervals || []).map((iv) => ({ s: wallMinutes(iv.start, shift.work_date), e: wallMinutes(iv.end, shift.work_date) }))
  const gapSegs = (m.gaps || []).map((g) => ({ s: wallMinutes(g.start, shift.work_date), e: wallMinutes(g.end, shift.work_date) }))
  const win = segs.length ? { s: Math.floor(segs[0].s / 60) * 60, e: Math.ceil(segs[segs.length - 1].e / 60) * 60 } : null
  const width = win ? Math.max(60, win.e - win.s) : 0
  const pct = (s: number, e: number) => ({ left: ((s - win!.s) / width) * 100 + '%', width: ((e - s) / width) * 100 + '%' })
  const tickCount = win ? Math.floor(width / 60) + 1 : 0
  const tickStep = tickCount > 13 ? 2 : 1

  const deductNote = unpaid
    ? deduction === 0
      ? `The ${unpaid}m unpaid break is already covered by the gap, so nothing is deducted again.`
      : m.gap_minutes === 0
        ? `No gap to reconcile against, so the full ${fmtDur(unpaid)} unpaid break is deducted.`
        : `Gap covers ${fmtDur(Math.min(m.gap_minutes, unpaid))} of the unpaid break; only ${fmtDur(deduction)} is deducted.`
    : 'No unpaid break set, so payable time equals recorded time.'

  // Captured vs now (§7f): the first-arrival record is never edited.
  const capRows: { f: string; cap: string; now: string }[] = []
  if (shift.captured) {
    const cs = shift.captured.sessions || []
    const n = Math.max(cs.length, shift.sessions.length)
    const lab = (t: string, a: string, b: string | null) => `${teamName(t)} ${a || '—'}–${b || '—'}`
    for (let i = 0; i < n; i++) {
      const c = cs[i]
      const s = shift.sessions[i]
      const cap = c ? lab(c.team_id, (c.start_time || '').slice(0, 5), c.finish_time ? c.finish_time.slice(0, 5) : null) : 'Not captured'
      const now = s ? lab(s.team_id, s.start_time, s.finish_time) : 'Removed'
      if (cap !== now) capRows.push({ f: `Session ${i + 1}`, cap, now })
    }
    const cb = shift.captured.breaks
    if (cb && cb.paid_minutes !== shift.paid_minutes) capRows.push({ f: 'Paid break', cap: cb.paid_minutes + 'm', now: shift.paid_minutes + 'm' })
    if (cb && cb.unpaid_minutes !== shift.unpaid_minutes) capRows.push({ f: 'Unpaid break', cap: cb.unpaid_minutes + 'm', now: shift.unpaid_minutes + 'm' })
  }

  const statePills: React.ReactNode[] = []
  if (shift.overlap_minutes > 0) statePills.push(<span key="o" className={styles.pillBad}>Overlap {fmtDur(shift.overlap_minutes)}</span>)
  const cross = shift.cross_date_overlaps || []
  for (const o of cross) {
    statePills.push(
      <span key={'x' + o.work_date} className={styles.pillBad}>
        Overlaps {shortDateLabel(o.work_date)} {fmtDur(o.minutes)}
      </span>
    )
  }
  if ((shift.metrics?.open_session_ids || []).length) statePills.push(<span key="m" className={styles.pillBad}>Missing clock-off</span>)
  if (shift.exclude_from_wages) statePills.push(<span key="x" className={styles.pillMuted}>Excluded from wages</span>)

  const excluded = shift.exclude_from_wages
  const n = shift.session_count

  return (
    <Overlay onBackdrop={tryClose} panelRef={panelEl}>
      <div className={`${o.drawerHead} ${ov.editorHead}`}>
        <div className={o.headText}>
          <h2 className={`${o.drawerTitle} ${ov.editorTitle}`}>{shift.worker_name}</h2>
          <p className={ov.editorMeta}>
            Work date {dateLabel(shift.work_date).replace(/ \d{4}$/, '')} · {n} session{n === 1 ? '' : 's'} · {fmtDur(shift.net_payable_minutes)} payable
          </p>
          <div className={ov.editorTags}>
            <span className={lastAdj ? `${ov.auditTag} ${ov.auditTagOn}` : ov.auditTag}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              {lastAdj ? `Adjusted by ${lastAdj.actor_email || lastAdj.actor} · ${fmtStamp(lastAdj.timestamp)}` : 'No adjustments on this shift'}
            </span>
            {statePills}
          </div>
        </div>
        <CloseX onClick={tryClose} disabled={busy} />
      </div>

      <div className={`${o.drawerBody} ${ov.editorBody} ${inertClass(busy, slow)}`}>
        {isLive && (
          <div className={styles.warn}>This worker has not clocked off. Anything entered now will be replaced when their real finish time arrives.</div>
        )}
        {m.overlap_minutes > 0 && (
          <div className={styles.bad}>
            Two sessions cover the same {fmtDur(m.overlap_minutes)}. Raw durations add to {fmtDur(m.raw_minutes)}, but only {fmtDur(m.recorded_minutes)} of real
            time was worked. Totals stay provisional until this is corrected.
          </div>
        )}

        {cross.length > 0 && (
          <div className={styles.bad}>
            {cross.map((o) => `${fmtDur(o.minutes)} of these times is also on ${shift.worker_name}’s shift for ${shortDateLabel(o.work_date)}.`).join(' ')}{' '}
            A different work date does not make it valid. Totals stay provisional until one of them is corrected.
          </div>
        )}

        <section>
          <div className={styles.sectionTitle}>Sessions</div>
          <div className={styles.sessHead}>
            <span>Team</span>
            <span>Start</span>
            <span>Finish</span>
            <span>Duration</span>
            <span />
          </div>
          {draft.sessions.map((s, i) => {
            const a = hm(s.start)
            const b = hm(s.finish)
            const len = a !== null && b !== null ? b + (s.finish_next_day ? 1440 : 0) - a : null
            const issues = issuesAt(i)
            const bad = issues.some((x) => x !== 'No clock-off captured')
            const opts = teamIds.includes(s.team_id) || !s.team_id ? teamIds : [s.team_id, ...teamIds]
            // The day switches show when a time could mean another day: a
            // finish at or before the start, or a session after one that
            // already crossed midnight.
            const startsNext = !!s.start_date && s.start_date > shift.work_date
            const afterNight = draft.sessions.slice(0, i).some((p) => p.finish_next_day || (!!p.start_date && p.start_date > shift.work_date))
            const showDay = s.finish_next_day || startsNext || afterNight || (len !== null && len <= 0)
            return (
              <div key={s.key} className={bad ? `${styles.sessRow} ${styles.sessRowBad}` : styles.sessRow} data-testid="session-row">
                <div className={styles.sessGrid}>
                  <select className={styles.field} value={s.team_id} onChange={(e) => setSess(i, 'team_id', e.target.value)} aria-label={`Session ${i + 1} team`}>
                    {!s.team_id && <option value="">Choose a team</option>}
                    {opts.map((id) => (
                      <option key={id} value={id}>
                        {teamName(id)}
                      </option>
                    ))}
                  </select>
                  <input
                    className={bad ? `${styles.field} ${styles.fieldBad}` : styles.field}
                    value={s.start}
                    placeholder="07:00"
                    inputMode="numeric"
                    aria-label={`Session ${i + 1} start`}
                    onChange={(e) => setSess(i, 'start', e.target.value)}
                  />
                  <input
                    className={bad ? `${styles.field} ${styles.fieldBad}` : styles.field}
                    value={s.finish}
                    placeholder="—"
                    inputMode="numeric"
                    aria-label={`Session ${i + 1} finish`}
                    onChange={(e) => setSess(i, 'finish', e.target.value)}
                  />
                  <span className={len !== null && len <= 0 ? `${styles.dur} ${styles.durBad}` : styles.dur}>
                    {len === null ? '—' : len <= 0 ? 'Invalid' : fmtDur(len)}
                    {(s.finish_next_day || startsNext) && <span className={styles.plusDay}>+1 day</span>}
                  </span>
                  {draft.sessions.length > 1 ? (
                    <button className={styles.iconBtn} title="Remove this session" aria-label={`Remove session ${i + 1}`} onClick={() => delSess(i)}>
                      ×
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
                {showDay && (
                  <div className={styles.dayToggles}>
                    {(startsNext || afterNight) && (
                      <label className={styles.dayToggle}>
                        <input
                          type="checkbox"
                          checked={startsNext}
                          aria-label={`Session ${i + 1} starts the next day`}
                          onChange={(e) => setNextDay(i, 'start', e.target.checked)}
                        />
                        Starts the next day
                      </label>
                    )}
                    <label className={styles.dayToggle}>
                      <input
                        type="checkbox"
                        checked={s.finish_next_day}
                        aria-label={`Session ${i + 1} finishes the next day`}
                        onChange={(e) => setNextDay(i, 'finish', e.target.checked)}
                      />
                      Finishes the next day
                    </label>
                  </div>
                )}
                {issues.length > 0 && <div className={styles.sessIssue}>{issues[0]}</div>}
                {(result?.zone_problems || [])
                  .filter((z) => z.index === i)
                  .map((z) => (
                    <div key={z.code + (z.which || '')} className={styles.sessIssue} role="alert" data-testid="zone-problem">
                      {z.error}
                      {z.code === 'AMBIGUOUS_LOCAL_TIME' && z.which && (
                        <div className={styles.dayToggles}>
                          {(z.options || []).map((o) => (
                            <label key={o.occurrence} className={styles.dayToggle}>
                              <input
                                type="radio"
                                name={`occ-${s.key}-${z.which}`}
                                aria-label={`Session ${i + 1} ${z.which}: ${o.label}`}
                                onChange={() => chooseOccurrence(i, z.which!, o.occurrence)}
                              />
                              {o.label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                {(result?.zone_unconfirmed || []).filter((o) => o.index === i && !(result?.zone_problems || []).some((z) => z.index === i && z.code === 'ZONE_CONFIRMATION_REQUIRED')).map((o) => (
                  <div key="offer" className={styles.dayToggles} data-testid="zone-confirm-offer">
                    {o.problem ? (
                      <div className={styles.sessIssue} role="alert">Time zone not confirmed, and none can be proposed: {o.problem.error}</div>
                    ) : (
                      <label className={styles.dayToggle}>
                        <input
                          type="checkbox"
                          checked={!!s.confirm_zone}
                          aria-label={`Session ${i + 1}: confirm the time zone without changing the times`}
                          onChange={(e) => confirmZone(i, e.target.checked)}
                        />
                        {o.kind === 'NO_ZONE' ? 'Time zone not recorded.' : 'Time zone set automatically, not chosen.'} Confirm these times were worked in{' '}
                        {o.proposed_zone} ({o.proposed_source_label}
                        {o.site_name ? `, ${o.site_name}` : ''}). No time or work date changes; the confirmation is recorded.
                      </label>
                    )}
                  </div>
                ))}
                {!(result?.zone_unconfirmed || []).some((o) => o.index === i) && (s.confirm_zone || (result?.zone_problems || []).some((z) => z.index === i && z.code === 'ZONE_CONFIRMATION_REQUIRED')) && (
                  <div className={styles.dayToggles}>
                    <label className={styles.dayToggle}>
                      <input
                        type="checkbox"
                        checked={!!s.confirm_zone}
                        aria-label={`Session ${i + 1}: confirm the time zone`}
                        onChange={(e) => confirmZone(i, e.target.checked)}
                      />
                      I confirm these times were worked in{' '}
                      {(result?.zone_problems || []).find((z) => z.index === i && z.code === 'ZONE_CONFIRMATION_REQUIRED')?.zone || zone?.zone || 'the zone shown'}
                    </label>
                  </div>
                )}
                <div className={styles.sessZone} data-testid="session-zone">
                  {sessionZoneNote(s.id ? shift.sessions.find((x) => x.id === s.id) : undefined, !s.id, zone, shiftZone)}
                </div>
              </div>
            )
          })}
          {(m.gaps || []).map((g, i) => (
            <div key={i} className={styles.gapRow}>
              Gap {fmtDur(g.minutes ?? 0)} · {g.start.slice(11, 16)} – {g.end.slice(11, 16)}
              {unpaid ? ` · counts toward the ${unpaid}m unpaid break` : ''}
            </div>
          ))}
          <button className={styles.addSess} onClick={addSess}>
            + Add session
          </button>
        </section>

        {win && (
          <section aria-label="Timeline">
            <div className={styles.timeline}>
              {gapSegs.map((g, i) => (
                <div key={'g' + i} className={styles.tlGap} style={pct(g.s, g.e)} />
              ))}
              {segs.map((s, i) => (
                <div key={'s' + i} className={styles.tlSeg} style={pct(s.s, s.e)} />
              ))}
            </div>
            <div className={styles.tlTicks}>
              {Array.from({ length: tickCount }, (_, i) => i)
                .filter((i) => i % tickStep === 0 || i === tickCount - 1)
                .map((i) => (
                  <span
                    key={i}
                    className={styles.tlTick}
                    style={{ left: ((i * 60) / width) * 100 + '%', transform: `translateX(${i === 0 ? '0' : i === tickCount - 1 ? '-100%' : '-50%'})` }}
                  >
                    {fmtHm(win.s + i * 60)}
                  </span>
                ))}
            </div>
          </section>
        )}

        <div className={styles.tiles}>
          <Tile label="Span" value={m.span_minutes ? fmtDur(m.span_minutes) : '—'} />
          <Tile label="Recorded" value={fmtDur(m.recorded_minutes)} />
          <Tile label="Gap" value={fmtDur(m.gap_minutes)} />
          <Tile label="Net payable" value={fmtDur(net)} net />
        </div>

        {shift.dst?.affected && (
          <section aria-label="Clock change" data-testid="dst-panel">
            <div className={styles.sectionTitle}>Clock change</div>
            <div className={shift.dst.calculation?.state === 'applied' ? styles.notice : styles.warn} data-testid="dst-summary">
              {dstSummary(shift.dst)}
            </div>
            <dl className={styles.dstFigures}>
              <dt>Wall-clock difference</dt>
              <dd data-testid="dst-wall">{fmtDur(shift.dst.wall_minutes ?? 0)}</dd>
              <dt>Actual elapsed time</dt>
              <dd data-testid="dst-elapsed">{fmtDur(shift.dst.elapsed_minutes ?? 0)}</dd>
              <dt>Normal hours entered</dt>
              <dd data-testid="dst-normal">{shift.dst.normal_minutes == null ? 'Must be entered' : fmtDur(shift.dst.normal_minutes)}</dd>
              {shift.dst.calculation?.state === 'applied' && (
                <>
                  <dt>Payable after the unpaid break</dt>
                  <dd data-testid="dst-payable">{fmtDur(shift.dst.calculation.payable_minutes)}</dd>
                </>
              )}
            </dl>
            {shift.dst.export_status && <div className={styles.sessIssue}>{shift.dst.export_status.message}</div>}
            {shift.dst.normal_minutes != null && (
              <div className={styles.sessZone}>
                Entered by {shift.dst.normal_supplied_by || 'unknown'}
                {shift.dst.normal_supplied_at ? ` · ${fmtStamp(shift.dst.normal_supplied_at)}` : ''}
                {shift.dst.normal_note ? ` · ${shift.dst.normal_note}` : ''}
              </div>
            )}
            {shift.dst.can_enter_normal_hours ? (
              <div style={{ marginTop: 8 }}>
                <div className={styles.sessZone}>
                  Normal hours are the gross length this person’s shift would normally have been on the clock, before any break is taken off. A manager enters them; they
                  are never worked out from the clock times. Changing or clearing them queues this day for recalculation.
                </div>
                <div className={styles.breakRow}>
                  <label className={styles.breakField}>
                    Normal hours
                    <input className={styles.field} value={nhH} inputMode="numeric" aria-label="Normal hours: hours" onChange={(e) => setNhH(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))} />
                  </label>
                  <label className={styles.breakField}>
                    Minutes
                    <input className={styles.field} value={nhM} inputMode="numeric" aria-label="Normal hours: minutes" onChange={(e) => setNhM(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))} />
                  </label>
                  <label className={styles.breakField}>
                    Note
                    <input className={styles.field} value={nhNote} aria-label="Normal hours: note" onChange={(e) => setNhNote(e.target.value)} />
                  </label>
                </div>
                <div className={styles.noticeActions}>
                  <button className={styles.smallBtn} disabled={nhBusy} onClick={() => saveNormalHours(false)}>
                    {shift.dst.normal_minutes == null ? 'Save normal hours' : 'Replace normal hours'}
                  </button>
                  {shift.dst.normal_minutes != null && (
                    <button className={styles.smallBtn} disabled={nhBusy} onClick={() => saveNormalHours(true)}>
                      Clear
                    </button>
                  )}
                </div>
                {nhMsg && <div className={styles.sessZone}>{nhMsg}</div>}
              </div>
            ) : (
              <div className={styles.sessZone}>Normal hours cannot be entered for this shift: only a spring crossing in a supported region takes them.</div>
            )}
          </section>
        )}

        <section>
          <div className={styles.sectionTitle}>Breaks</div>
          {draft.breaks.recomputed && (
            <div className={styles.notice} style={{ marginBottom: 10 }}>
              {draft.breaks.wasManual
                ? 'The hours changed, so the schedule was re-applied and replaced your earlier manual break: '
                : 'Breaks recalculated from the schedule for these hours: '}
              {toMin(draft.breaks.paid)}m paid, {toMin(draft.breaks.unpaid)}m unpaid. Review it, or type over either figure to override.
              <div className={styles.noticeActions}>
                {draft.breaks.prior && (
                  <button className={styles.smallBtn} onClick={restoreBreak}>
                    Restore {toMin(draft.breaks.prior.paid)}/{toMin(draft.breaks.prior.unpaid)}
                  </button>
                )}
                <button className={styles.smallBtn} onClick={ackBreak}>
                  Reviewed
                </button>
              </div>
            </div>
          )}
          <div className={styles.breakRow}>
            <label className={styles.breakField}>
              Paid break (min)
              <input
                className={draft.breaks.recomputed ? `${styles.field} ${styles.fieldFlag}` : styles.field}
                value={draft.breaks.paid}
                inputMode="numeric"
                onChange={(e) => setBr('paid', e.target.value)}
              />
            </label>
            <label className={styles.breakField}>
              Unpaid break (min)
              <input
                className={draft.breaks.recomputed ? `${styles.field} ${styles.fieldFlag}` : styles.field}
                value={draft.breaks.unpaid}
                inputMode="numeric"
                onChange={(e) => setBr('unpaid', e.target.value)}
              />
            </label>
            <span className={draft.breaks.manual ? styles.pillAccent : styles.pillGood}>{draft.breaks.manual ? 'Manual' : 'Computed'}</span>
            {draft.breaks.manual && (
              <button className={styles.linkBtn} onClick={resetBr}>
                Return to computed
              </button>
            )}
          </div>
          <div className={styles.deduct}>{deductNote}</div>
        </section>

        <section>
          <label className={styles.label} htmlFor="ts-reason">
            Note or reason: saved with the adjustment, or on its own as a comment if nothing else changed.
          </label>
          <input id="ts-reason" className={styles.pillField} value={reason} placeholder="Second break taken, not captured" onChange={(e) => setReason(e.target.value)} />
        </section>

        <div className={excluded ? `${styles.switchWrap} ${styles.switchWrapOff}` : styles.switchWrap}>
          <button
            role="switch"
            aria-checked={!excluded}
            aria-label="Included in wages"
            className={excluded ? `${styles.switchTrack} ${styles.switchTrackOff}` : styles.switchTrack}
            onClick={toggleExclude}
          >
            <span className={styles.switchKnob} />
          </button>
          <div>
            <div className={styles.switchTitle}>{excluded ? 'Excluded from wages' : 'Included in wages'}</div>
            <div className={styles.switchNote}>
              {excluded
                ? 'These hours are recorded but will not be paid or exported to payroll.'
                : 'These hours will be paid and exported to payroll as normal.'}
            </div>
          </div>
        </div>

        {capRows.length > 0 && (
          <section>
            <div className={styles.sectionTitle}>As captured, and as it stands now</div>
            <div className={styles.capSub}>Adjustments take precedence in the totals. The captured record is kept unchanged.</div>
            <div className={styles.capTable}>
              <div className={styles.capHead}>
                <span>Field</span>
                <span>Captured</span>
                <span>Now</span>
              </div>
              {capRows.map((r) => (
                <div key={r.f} className={styles.capRow}>
                  <span>{r.f}</span>
                  <span>{r.cap}</span>
                  <span>{r.now}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className={styles.sectionTitle}>History and comments</div>
          {history.length === 0 ? (
            <div className={styles.deduct}>Nothing recorded against this shift yet.</div>
          ) : (
            <div className={styles.events}>
              {[...history].reverse().map((ev) => (
                <div key={ev.id} className={isAdjustment(ev) ? `${styles.event} ${styles.eventAdj}` : styles.event} data-testid="history-event">
                  <div className={styles.eventHead}>
                    {isCrewEntry(ev) ? (
                      <span className={styles.kindCrew}>Dataphyll Crew</span>
                    ) : (
                      <>
                        <span className={ev.type === 'comment' ? styles.kindComment : styles.kindAdj}>{ev.type === 'comment' ? 'Comment' : 'Adjustment'}</span>
                        <span>{ev.actor_email || ev.actor}</span>
                      </>
                    )}
                    <span>· {fmtStamp(ev.timestamp)}</span>
                    {ev.can_undo && (
                      <button
                        className={styles.undoBtn}
                        onClick={() => undo(ev)}
                        disabled={dirty}
                        title={dirty ? 'Save or discard your edits first' : isCrewEntry(ev) ? 'Take back what the phone recorded' : 'Reverse this adjustment'}
                      >
                        Undo
                      </button>
                    )}
                  </div>
                  {(ev.changes || []).map((c, i) => (
                    <div key={i} className={styles.change}>
                      <span className={styles.changeField}>{c.f}</span>
                      <span className={styles.changeFrom}>{c.from}</span>
                      <span>›</span>
                      <span className={styles.changeTo}>{c.to}</span>
                    </div>
                  ))}
                  {eventReason(ev) && <div className={styles.eventReason}>{eventReason(ev)}</div>}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className={o.drawerFoot}>
        {dirty && <span className={ov.unsaved}>Unsaved changes</span>}
        <Failed text={error} />
        <button className={`${o.ghost} ${ov.pushRight}`} onClick={tryClose} disabled={busy}>
          Cancel
        </button>
        <PendingButton className={`${o.primary} ${ov.noAuto}`} onClick={save} disabled={false} busy={busy} pending="Saving…">
          Save changes
        </PendingButton>
      </div>

      {confirm}
    </Overlay>
  )
}

// The editor is a right-hand drawer (V4 tsEditOpen, 620px wide).
function Overlay({ children, onBackdrop, panelRef }: { children: React.ReactNode; onBackdrop: () => void; panelRef: React.RefObject<HTMLElement> }) {
  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && onBackdrop()}>
      <aside ref={panelRef} tabIndex={-1} className={`${o.drawer} ${ov.editorDrawer}`} role="dialog" aria-label="Shift editor">
        {children}
      </aside>
    </div>
  )
}

function CloseX({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={o.close} onClick={onClick} aria-label="Close" disabled={disabled}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </button>
  )
}

function Tile({ label, value, net }: { label: string; value: string; net?: boolean }) {
  return (
    <div className={net ? `${styles.tile} ${styles.tileNet}` : styles.tile}>
      <div className={styles.tileLabel}>{label}</div>
      <div className={styles.tileValue}>{value}</div>
    </div>
  )
}
